import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  Input,
  Link,
  Modal,
  Multiselect,
  Pagination,
  Select,
  SegmentedControl,
  SpaceBetween,
  StatusIndicator,
  Textarea,
  TextFilter,
} from '@cloudscape-design/components';
import { PageLayout, PlatformTable, EmptyState, useAuth, useNotify, createApiClient } from '@deloitte-us-consulting/gp-sdk';
import { useActiveAllianceOptions } from '../rbac/useActiveAlliances';
import DataReviewModal from './catalog/DataReviewModal';
import { systemName } from './data/systems';
import { repoLabel } from './appsModel';
import type { DataGrant } from './catalog/types';

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';

interface Option { value: string; label: string }
type Api = ReturnType<typeof createApiClient>;

interface OnboardingRequest {
  id: string;
  appId: string;
  displayName: string;
  repoUrl: string;
  pointOfContactEmail: string;
  pointOfContactName?: string;
  description: string;
  appType: 'hosted' | 'linked';
  externalUrl?: string;
  requiredRole?: string[];
  requiredAlliance?: string[];
  wantsAi: boolean;
  /** Requests submitted before the data catalog carry this instead of dataSources. */
  wantsAirtable?: boolean;
  /** Whole systems the builder asked for (AirTable, ...). */
  dataSystems?: string[];
  /** Tables named directly: older requests, or added by the platform team. */
  dataSources: string[];
  dataNeed?: string;
  dataReview: { status: 'not_needed' | 'pending' | 'complete'; grants?: DataGrant[]; notes?: string };
  targetGoLive?: string;
  attestedFromTemplate: boolean;
  submittedAt: string;
  submitterId: string;
  status: 'submitted' | 'approved' | 'rejected';
  decidedBy?: string;
  decidedAt?: string;
  rejectionReason?: string;
}

/** What data the request asked for, and where the data review stands. */
export function dataCell(r: Pick<OnboardingRequest, 'wantsAirtable' | 'dataSources' | 'dataReview'> & { dataSystems?: string[] }) {
  const systems = r.dataSystems ?? [];
  if (systems.length === 0 && r.dataSources.length === 0) return r.wantsAirtable ? 'AirTable (legacy)' : '—';
  const label = systems.length > 0 ? systems.map(systemName).join(', ') : `${r.dataSources.length} table${r.dataSources.length === 1 ? '' : 's'}`;
  return r.dataReview.status === 'complete' ? (
    <StatusIndicator type="success">{label}, reviewed</StatusIndicator>
  ) : (
    <StatusIndicator type="pending">{label}, review needed</StatusIndicator>
  );
}

const PAGE_SIZE = 20;

export type StatusTab = OnboardingRequest['status'] | 'all';
export interface RequestFilters {
  text: string;
  status: StatusTab;
  /** '' = any. */
  type: OnboardingRequest['appType'] | '';
  /** 'needs_review' = a data review is still pending. */
  data: 'needs_review' | '';
}
const INITIAL_FILTERS: RequestFilters = { text: '', status: 'submitted', type: '', data: '' };

const TYPE_OPTIONS = [
  { value: '', label: 'Any type' },
  { value: 'hosted', label: 'Hosted' },
  { value: 'linked', label: 'Linked' },
];
const DATA_OPTIONS = [
  { value: '', label: 'Data: any' },
  { value: 'needs_review', label: 'Needs data review' },
];

/** Client-side filter for the review queue. */
export function filterRequests(requests: OnboardingRequest[], f: RequestFilters): OnboardingRequest[] {
  const q = f.text.trim().toLowerCase();
  return requests.filter((r) => {
    if (f.status !== 'all' && r.status !== f.status) return false;
    if (f.type && r.appType !== f.type) return false;
    if (f.data === 'needs_review' && r.dataReview.status !== 'pending') return false;
    if (!q) return true;
    return [r.appId, r.displayName, r.repoUrl, r.pointOfContactName, r.pointOfContactEmail, r.submitterId].some((v) =>
      v?.toLowerCase().includes(q),
    );
  });
}

function statusIndicator(status: OnboardingRequest['status']) {
  if (status === 'submitted') return <StatusIndicator type="pending" wrapText={false}>Submitted</StatusIndicator>;
  if (status === 'approved') return <StatusIndicator type="success" wrapText={false}>Approved</StatusIndicator>;
  return <StatusIndicator type="error" wrapText={false}>Rejected</StatusIndicator>;
}

/** Same catalog OnboardSubmit.tsx offers a builder — needed here too, to turn
 *  a submission's declared requiredRole ids back into readable labels, and to
 *  let an admin edit the final scope as a picklist instead of free-text ids. */
function usePlatformRoleOptions(api: Api): Option[] {
  const [options, setOptions] = useState<Option[]>([]);
  useEffect(() => {
    api
      .get<{ id: string; role_name: string }[]>('/api/rbac/role?scope=platform')
      .then((roles) => setOptions(roles.map((r) => ({ value: r.id, label: r.role_name }))))
      .catch(() => setOptions([]));
  }, [api]);
  return options;
}

/** Read-only scope tags — looks up the human label for each role/alliance id
 *  (falling back to the raw id if the catalog doesn't have it, e.g. a role
 *  that's since been deleted) instead of a comma-joined string of ids. */
function ScopeTags({ roleIds, allianceNames, roleOptions, allianceOptions }: {
  roleIds: string[];
  allianceNames: string[];
  roleOptions: Option[];
  allianceOptions: Option[];
}) {
  if (roleIds.length === 0 && allianceNames.length === 0) {
    return <Box color="text-body-secondary">Everyone</Box>;
  }
  return (
    <SpaceBetween direction="horizontal" size="xxs">
      {roleIds.map((id) => (
        <Badge key={`role-${id}`} color="blue">{roleOptions.find((o) => o.value === id)?.label ?? id}</Badge>
      ))}
      {allianceNames.map((name) => (
        <Badge key={`alliance-${name}`} color="grey">{allianceOptions.find((o) => o.value === name)?.label ?? name}</Badge>
      ))}
    </SpaceBetween>
  );
}

/** Every field captured by the intake form (OnboardSubmit.tsx), read-only —
 *  shown both inside the Approve modal (so the admin can see everything the
 *  builder declared before confirming the registry fields below it) and in
 *  the plain "View" modal for a submission that's already been decided. */
function SubmissionDetails({ request, roleOptions, allianceOptions }: {
  request: OnboardingRequest;
  roleOptions: Option[];
  allianceOptions: Option[];
}) {
  return (
    <SpaceBetween size="l">
      <ColumnLayout columns={2} variant="text-grid">
        <div>
          <Box variant="awsui-key-label">App ID</Box>
          <Box>{request.appId}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Type</Box>
          <Box>{request.appType === 'linked' ? 'Linked' : 'Hosted'}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Repo</Box>
          <Link external href={request.repoUrl}>{repoLabel(request.repoUrl)}</Link>
        </div>
        {request.appType === 'linked' && request.externalUrl && (
          <div>
            <Box variant="awsui-key-label">External URL</Box>
            <Link external href={request.externalUrl}>{request.externalUrl}</Link>
          </div>
        )}
        <div>
          <Box variant="awsui-key-label">Point of contact</Box>
          <Box>
            {request.pointOfContactName
              ? `${request.pointOfContactName} (${request.pointOfContactEmail})`
              : request.pointOfContactEmail}
          </Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Submitted</Box>
          <Box>{request.submittedAt} by {request.submitterId}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Wants AI assistant</Box>
          <Box>{request.wantsAi ? 'Yes' : 'No'}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Data</Box>
          <Box>
            {(request.dataSystems ?? []).length
              ? (request.dataSystems ?? []).map(systemName).join(', ')
              : request.dataSources.length
                ? request.dataSources.join(', ')
                : request.wantsAirtable
                ? 'AirTable (requested before the data catalog)'
                : 'None'}
          </Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Target go-live</Box>
          <Box>{request.targetGoLive || '—'}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Template attested</Box>
          <Box>{request.appType === 'hosted' ? (request.attestedFromTemplate ? 'Yes' : 'No') : 'N/A (linked app)'}</Box>
        </div>
      </ColumnLayout>
      <div>
        <Box variant="awsui-key-label">Description</Box>
        <Box>{request.description}</Box>
      </div>
      {(request.dataSources.length > 0 || (request.dataSystems ?? []).length > 0) && (
        <ColumnLayout columns={2} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">What the data is for</Box>
            <Box>{request.dataNeed || '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Data review</Box>
            {dataCell(request)}
            {request.dataReview.notes && <Box color="text-body-secondary">{request.dataReview.notes}</Box>}
          </div>
        </ColumnLayout>
      )}
      <div>
        <Box variant="awsui-key-label">Requested scope</Box>
        <ScopeTags
          roleIds={request.requiredRole ?? []}
          allianceNames={request.requiredAlliance ?? []}
          roleOptions={roleOptions}
          allianceOptions={allianceOptions}
        />
      </div>
      {request.status !== 'submitted' && (
        <ColumnLayout columns={2} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Status</Box>
            {statusIndicator(request.status)}
          </div>
          <div>
            <Box variant="awsui-key-label">{request.status === 'approved' ? 'Approved by' : 'Rejected by'}</Box>
            <Box>{request.decidedBy ?? '—'}{request.decidedAt ? ` on ${request.decidedAt}` : ''}</Box>
          </div>
          {request.status === 'rejected' && (
            <div>
              <Box variant="awsui-key-label">Reason</Box>
              <Box>{request.rejectionReason || '—'}</Box>
            </div>
          )}
        </ColumnLayout>
      )}
    </SpaceBetween>
  );
}

/**
 * The admin review queue for app onboarding submissions — the decision side
 * of the onboarding-request domain (api/src/onboarding/requests.ts). Gated
 * identically to App Registry (Registry:Manage): approving here writes a
 * DISABLED registry entry (prefilled from the submission, editable in the
 * modal below) — it does not go live. The admin still completes the
 * deploy-role/SDK-access/first-deploy gates from docs/onboarding-an-app.md
 * outside this app, then enables the app from App Registry as today.
 */
export default function OnboardingAdmin() {
  const { accessToken } = useAuth();
  const { notify } = useNotify();
  const api = useMemo(() => createApiClient(API_URL, () => accessToken), [accessToken]);
  const roleOptions = usePlatformRoleOptions(api);
  const allianceOptions = useActiveAllianceOptions(api);

  const [requests, setRequests] = useState<OnboardingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<RequestFilters>(INITIAL_FILTERS);
  const [pageIndex, setPageIndex] = useState(1);

  // The open submission lives in the URL (?request=<id>) so it can be linked to —
  // App Registry's "Intake submission" link lands here.
  const [params, setParams] = useSearchParams();
  const viewingId = params.get('request');
  const viewing = viewingId ? (requests.find((r) => r.id === viewingId) ?? null) : null;
  const setViewing = (r: OnboardingRequest | null) => setParams(r ? { request: r.id } : {});

  const [approving, setApproving] = useState<OnboardingRequest | null>(null);
  const [approveForm, setApproveForm] = useState<{
    displayName: string;
    requiredRole: Option[];
    requiredAlliance: Option[];
    aiEnabled: boolean;
  }>({ displayName: '', requiredRole: [], requiredAlliance: [], aiEnabled: false });
  const [approveSaving, setApproveSaving] = useState(false);

  const [reviewing, setReviewing] = useState<OnboardingRequest | null>(null);

  const [rejecting, setRejecting] = useState<OnboardingRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSaving, setRejectSaving] = useState(false);

  const load = async () => {
    setError(null);
    try {
      setRequests(await api.get<OnboardingRequest[]>('/api/onboarding/all'));
    } catch (e) {
      setError((e as Error).message);
      notify({ type: 'error', header: 'Could not load onboarding submissions', content: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [api]);

  const openApprove = (r: OnboardingRequest) => {
    setApproving(r);
    setApproveForm({
      displayName: r.displayName,
      requiredRole: (r.requiredRole ?? []).map((id) => roleOptions.find((o) => o.value === id) ?? { value: id, label: id }),
      requiredAlliance: (r.requiredAlliance ?? []).map((name) => allianceOptions.find((o) => o.value === name) ?? { value: name, label: name }),
      aiEnabled: r.wantsAi,
    });
  };

  const confirmApprove = async () => {
    if (!approving) return;
    setApproveSaving(true);
    try {
      await api.post(`/api/onboarding/${approving.id}/approve`, {
        displayName: approveForm.displayName.trim(),
        requiredRole: approveForm.requiredRole.map((o) => o.value),
        requiredAlliance: approveForm.requiredAlliance.map((o) => o.value),
        aiEnabled: approveForm.aiEnabled,
      });
      notify({
        type: 'success',
        header: 'Registered (disabled)',
        content: `${approving.appId} was added to the registry. Finish the deploy-role/SDK-access/first-deploy gates, then enable it from App Registry.`,
      });
      setApproving(null);
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Approve failed', content: (e as Error).message });
    } finally {
      setApproveSaving(false);
    }
  };

  const confirmReject = async () => {
    if (!rejecting) return;
    setRejectSaving(true);
    try {
      await api.post(`/api/onboarding/${rejecting.id}/reject`, { reason: rejectReason.trim() || undefined });
      notify({ type: 'success', header: 'Rejected', content: `${rejecting.appId} was rejected.` });
      setRejecting(null);
      setRejectReason('');
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Reject failed', content: (e as Error).message });
    } finally {
      setRejectSaving(false);
    }
  };

  const filtered = filterRequests(requests, filters);
  const pagesCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(pageIndex, pagesCount);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const setFilter = (patch: Partial<RequestFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPageIndex(1);
  };
  const extraFilters = filters.text !== '' || filters.type !== '' || filters.data !== '';
  const clearExtraFilters = () => setFilter({ text: '', type: '', data: '' });
  const countOf = (s: OnboardingRequest['status']) => requests.filter((r) => r.status === s).length;

  return (
    <PageLayout
      title="App Onboarding"
      description="Review submissions from prospective app builders. Approving registers the app (disabled) for the platform team to finish infra setup."
    >
      <PlatformTable
        items={pageItems}
        loading={loading}
        error={error}
        onRetry={load}
        title="Submissions"
        totalCount={filtered.length}
        wrapLines
        filter={
          <SpaceBetween size="xs">
            <SegmentedControl
              selectedId={filters.status}
              label="Submission status"
              onChange={({ detail }) => setFilter({ status: detail.selectedId as StatusTab })}
              options={[
                { id: 'submitted', text: `Pending (${countOf('submitted')})` },
                { id: 'approved', text: `Approved (${countOf('approved')})` },
                { id: 'rejected', text: `Rejected (${countOf('rejected')})` },
                { id: 'all', text: `All (${requests.length})` },
              ]}
            />
            <SpaceBetween size="xs" direction="horizontal">
              <TextFilter
                filteringText={filters.text}
                filteringPlaceholder="Search by name, ID, repo or contact"
                filteringAriaLabel="Search submissions"
                countText={extraFilters ? `${filtered.length} ${filtered.length === 1 ? 'match' : 'matches'}` : ''}
                onChange={({ detail }) => setFilter({ text: detail.filteringText })}
              />
              <Select
                selectedOption={TYPE_OPTIONS.find((o) => o.value === filters.type) ?? TYPE_OPTIONS[0] ?? null}
                options={TYPE_OPTIONS}
                ariaLabel="Type"
                onChange={({ detail }) => setFilter({ type: (detail.selectedOption.value ?? '') as RequestFilters['type'] })}
              />
              <Select
                selectedOption={DATA_OPTIONS.find((o) => o.value === filters.data) ?? DATA_OPTIONS[0] ?? null}
                options={DATA_OPTIONS}
                ariaLabel="Data"
                onChange={({ detail }) => setFilter({ data: (detail.selectedOption.value ?? '') as RequestFilters['data'] })}
              />
              {extraFilters && <Button variant="link" onClick={clearExtraFilters}>Clear filters</Button>}
            </SpaceBetween>
          </SpaceBetween>
        }
        pagination={<Pagination currentPageIndex={currentPage} pagesCount={pagesCount} onChange={({ detail }) => setPageIndex(detail.currentPageIndex)} />}
        empty={
          requests.length === 0 ? (
            <EmptyState title="No submissions yet" description="Submissions from the intake form appear here." />
          ) : extraFilters ? (
            <EmptyState
              title="No submissions match these filters"
              description="Try a different search, or clear the filters."
              action={<Button onClick={clearExtraFilters}>Clear filters</Button>}
            />
          ) : filters.status === 'submitted' ? (
            <EmptyState title="Nothing pending" description="Every submission has been decided." />
          ) : (
            <EmptyState title="Nothing here" description="No submissions have this status." />
          )
        }
        columnDefinitions={[
          {
            header: 'App',
            cell: (r) => (
              <div>
                <div><strong>{r.displayName}</strong></div>
                <Box variant="small" color="text-body-secondary">{r.appId}</Box>
              </div>
            ),
          },
          { header: 'Repo', cell: (r) => <Link external href={r.repoUrl}>{repoLabel(r.repoUrl)}</Link> },
          { header: 'Requested by', cell: (r) => r.pointOfContactName || r.pointOfContactEmail },
          { header: 'Type', cell: (r) => (r.appType === 'linked' ? 'Linked' : 'Hosted') },
          {
            header: 'Scope',
            cell: (r) => (
              <ScopeTags
                roleIds={r.requiredRole ?? []}
                allianceNames={r.requiredAlliance ?? []}
                roleOptions={roleOptions}
                allianceOptions={allianceOptions}
              />
            ),
          },
          {
            header: 'Asks for',
            cell: (r) => (
              <SpaceBetween size="xxs">
                {r.wantsAi && <Badge color="blue">AI assistant</Badge>}
                {dataCell(r)}
              </SpaceBetween>
            ),
          },
          { header: 'Submitted', cell: (r) => r.submittedAt.slice(0, 10) },
          {
            header: 'Status',
            cell: (r) => (
              <div>
                {statusIndicator(r.status)}
                {r.decidedBy && <Box variant="small" color="text-body-secondary">by {r.decidedBy}</Box>}
              </div>
            ),
          },
          {
            header: 'Actions',
            cell: (r) => (
              <SpaceBetween size="xs" direction="horizontal">
                <Button variant="inline-link" onClick={() => setViewing(r)}>View</Button>
                {r.status === 'submitted' && (
                  <>
                    {r.dataReview.status !== 'not_needed' && (
                      <Button variant="inline-link" onClick={() => setReviewing(r)}>
                        {r.dataReview.status === 'pending' ? 'Data review' : 'Edit data review'}
                      </Button>
                    )}
                    <Button variant="inline-link" disabled={r.dataReview.status === 'pending'} onClick={() => openApprove(r)}>
                      Approve
                    </Button>
                    <Button variant="inline-link" onClick={() => setRejecting(r)}>Reject</Button>
                  </>
                )}
              </SpaceBetween>
            ),
          },
        ]}
      />

      <Modal
        visible={viewing !== null}
        onDismiss={() => setViewing(null)}
        header={viewing ? `${viewing.displayName} (${viewing.appId})` : ''}
        footer={
          <Box float="right">
            <Button variant="link" onClick={() => setViewing(null)}>Close</Button>
          </Box>
        }
      >
        {viewing && <SubmissionDetails request={viewing} roleOptions={roleOptions} allianceOptions={allianceOptions} />}
      </Modal>

      <Modal
        visible={approving !== null}
        onDismiss={() => setApproving(null)}
        header={approving ? `Approve ${approving.appId}` : ''}
        footer={
          <Box float="right">
            <SpaceBetween size="xs" direction="horizontal">
              <Button variant="link" onClick={() => setApproving(null)}>Cancel</Button>
              <Button variant="primary" loading={approveSaving} onClick={confirmApprove}>Register (disabled)</Button>
            </SpaceBetween>
          </Box>
        }
      >
        {approving && (
          <SpaceBetween size="l">
            <Box color="text-body-secondary">
              This creates a <strong>disabled</strong> registry entry for <code>{approving.appId}</code>. Everything the
              builder submitted is below — review it, then confirm the registry fields underneath (prefilled from the
              submission; you can still change them later in App Registry).
            </Box>
            <SubmissionDetails request={approving} roleOptions={roleOptions} allianceOptions={allianceOptions} />
            <Box variant="h3">Registry fields</Box>
            <SpaceBetween size="s">
              <Box variant="awsui-key-label">Display name</Box>
              <Input value={approveForm.displayName} onChange={({ detail }) => setApproveForm((f) => ({ ...f, displayName: detail.value }))} />
            </SpaceBetween>
            <SpaceBetween size="s">
              <Box variant="awsui-key-label">Required role(s)</Box>
              <Multiselect
                selectedOptions={approveForm.requiredRole}
                placeholder="Choose one or more roles, or leave blank for everyone"
                options={roleOptions}
                onChange={({ detail }) => setApproveForm((f) => ({ ...f, requiredRole: detail.selectedOptions as Option[] }))}
                filteringType="auto"
              />
            </SpaceBetween>
            <SpaceBetween size="s">
              <Box variant="awsui-key-label">Required alliance(s)</Box>
              <Multiselect
                selectedOptions={approveForm.requiredAlliance}
                placeholder="Choose one or more alliances, or leave blank for everyone"
                options={allianceOptions}
                onChange={({ detail }) => setApproveForm((f) => ({ ...f, requiredAlliance: detail.selectedOptions as Option[] }))}
                filteringType="auto"
              />
            </SpaceBetween>
            <Checkbox checked={approveForm.aiEnabled} onChange={({ detail }) => setApproveForm((f) => ({ ...f, aiEnabled: detail.checked }))}>
              AI enabled
            </Checkbox>
          </SpaceBetween>
        )}
      </Modal>

      {reviewing && (
        <DataReviewModal
          api={api}
          request={reviewing}
          onDismiss={() => setReviewing(null)}
          onSaved={() => {
            setReviewing(null);
            void load();
          }}
        />
      )}

      <Modal
        visible={rejecting !== null}
        onDismiss={() => setRejecting(null)}
        header={rejecting ? `Reject ${rejecting.appId}` : ''}
        footer={
          <Box float="right">
            <SpaceBetween size="xs" direction="horizontal">
              <Button variant="link" onClick={() => setRejecting(null)}>Cancel</Button>
              <Button variant="primary" loading={rejectSaving} onClick={confirmReject}>Reject</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          <Box variant="awsui-key-label">Reason (shown to the submitter)</Box>
          <Textarea value={rejectReason} onChange={({ detail }) => setRejectReason(detail.value)} rows={3} />
        </SpaceBetween>
      </Modal>
    </PageLayout>
  );
}
